import React, { useState, useEffect } from "react";
import { apiFetch } from "../services/api";

const TenantManagement = ({ user }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingOrg, setEditingOrg] = useState(null);
  const [editForm, setEditForm] = useState({
    trial_days: 30,
    ai_document_generation_limit: -1,
    max_users: -1,
    llm_monthly_cost_quota_usd: "5",
    llm_quota_enforce: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiFetch("/api/admin/organizations");
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to fetch organizations");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org.id);
    const rawCost = org.settings?.llm_monthly_cost_quota_usd;
    const cost = rawCost === null || rawCost === undefined ? 5 : rawCost;
    setEditForm({
      trial_days: org.settings.trial_days,
      ai_file_processing_limit: -1,
      text_ai_processing_limit: -1,
      ai_document_generation_limit: org.settings.ai_document_generation_limit,
      max_users: org.settings.max_users,
      llm_monthly_cost_quota_usd: String(cost),
      llm_quota_enforce: Boolean(org.settings?.llm_quota_enforce ?? true),
    });
  };

  const handleCancel = () => {
    setEditingOrg(null);
    setEditForm({
      trial_days: 30,
      ai_document_generation_limit: -1,
      max_users: -1,
      llm_monthly_cost_quota_usd: "5",
      llm_quota_enforce: true,
    });
  };

  const handleSave = async (orgId) => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        trial_days: editForm.trial_days,
        ai_file_processing_limit: -1,
        text_ai_processing_limit: -1,
        ai_document_generation_limit: editForm.ai_document_generation_limit,
        max_users: editForm.max_users,
        llm_monthly_cost_quota_usd:
          editForm.llm_monthly_cost_quota_usd === "" ||
          editForm.llm_monthly_cost_quota_usd === null ||
          editForm.llm_monthly_cost_quota_usd === undefined
            ? 5
            : parseFloat(editForm.llm_monthly_cost_quota_usd),
        llm_quota_enforce: Boolean(editForm.llm_quota_enforce),
      };

      const response = await apiFetch(
        `/api/admin/organization/${orgId}/settings`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        setSuccess("Settings updated successfully!");
        setEditingOrg(null);
        await fetchOrganizations();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to update settings");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatLimit = (value) => {
    if (value === -1 || value === null || value === undefined) {
      return "Unlimited";
    }
    return value.toLocaleString();
  };

  const formatCostQuota = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "$5.00";
    return `$${n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const filteredOrganizations = organizations.filter((org) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      org.name.toLowerCase().includes(searchLower) ||
      org.admin_email?.toLowerCase().includes(searchLower) ||
      org.id.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="ml-3 text-gray-300">Loading organizations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">
          🏢 Tenant Management
        </h1>
        <p className="text-gray-400 text-sm">
          Manage limits and settings for each organization/tenant
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-900/50 border border-red-600 rounded-md text-red-200">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-900/50 border border-green-600 rounded-md text-green-200 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          {success}
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by organization name, admin email, or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* Statistics */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Total Organizations</div>
          <div className="text-2xl font-bold text-white">
            {organizations.length}
          </div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Active Organizations</div>
          <div className="text-2xl font-bold text-green-400">
            {organizations.filter((o) => o.is_active).length}
          </div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Total Users</div>
          <div className="text-2xl font-bold text-blue-400">
            {organizations.reduce(
              (sum, o) => sum + (o.current_users_count || 0),
              0
            )}
          </div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Filtered Results</div>
          <div className="text-2xl font-bold text-white">
            {filteredOrganizations.length}
          </div>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Admin
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Trial Days
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Doc Gen Limit
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Max Users
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  LLM $ / mo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredOrganizations.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    No organizations found
                  </td>
                </tr>
              ) : (
                filteredOrganizations.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <div className="text-sm text-white font-medium">
                        {org.name}
                      </div>
                      <div className="text-xs text-gray-400">{org.id}</div>
                      {!org.is_active && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-red-600 text-white text-xs rounded">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-300">
                        {org.admin_name || "N/A"}
                      </div>
                      <div className="text-xs text-gray-400">
                        {org.admin_email || "N/A"}
                      </div>
                      {org.admin_is_trial_user && (
                        <div className="mt-1">
                          <span className="inline-block px-2 py-0.5 bg-blue-600 text-white text-xs rounded">
                            🧪 Trial
                            {org.trial_days_remaining !== null &&
                              org.trial_days_remaining !== undefined && (
                                <span className="ml-1">
                                  ({org.trial_days_remaining} days left)
                                </span>
                              )}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {org.current_users_count || 0}
                      {org.settings.max_users !== -1 && (
                        <span className="text-gray-500">
                          {" "}
                          / {formatLimit(org.settings.max_users)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingOrg === org.id ? (
                        <input
                          type="number"
                          min="0"
                          value={editForm.trial_days}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              trial_days: parseInt(e.target.value) || 0,
                            })
                          }
                          className="w-20 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                        />
                      ) : (
                        <span className="text-sm text-gray-300">
                          {org.settings.trial_days} days
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingOrg === org.id ? (
                        <input
                          type="number"
                          min="-1"
                          value={editForm.ai_document_generation_limit}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              ai_document_generation_limit:
                                parseInt(e.target.value) || -1,
                            })
                          }
                          className="w-24 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                          placeholder="-1 for unlimited"
                        />
                      ) : (
                        <span className="text-sm text-gray-300">
                          {formatLimit(
                            org.settings.ai_document_generation_limit
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingOrg === org.id ? (
                        <input
                          type="number"
                          min="-1"
                          value={editForm.max_users}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              max_users: parseInt(e.target.value) || -1,
                            })
                          }
                          className="w-24 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                          placeholder="-1 for unlimited"
                        />
                      ) : (
                        <span className="text-sm text-gray-300">
                          {formatLimit(org.settings.max_users)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingOrg === org.id ? (
                        <div className="space-y-1">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editForm.llm_monthly_cost_quota_usd}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                llm_monthly_cost_quota_usd: e.target.value,
                              })
                            }
                            className="w-28 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                            placeholder="5.00"
                          />
                          <label className="flex items-center gap-1 text-xs text-gray-300">
                            <input
                              type="checkbox"
                              checked={Boolean(editForm.llm_quota_enforce)}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  llm_quota_enforce: e.target.checked,
                                })
                              }
                            />
                            Enforce
                          </label>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-300">
                          {formatCostQuota(
                            org.settings?.llm_monthly_cost_quota_usd
                          )}
                          {org.settings?.llm_quota_enforce ? (
                            <span className="ml-1 text-xs text-amber-400">
                              enforced
                            </span>
                          ) : null}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingOrg === org.id ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleSave(org.id)}
                            disabled={saving}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
                          >
                            {saving ? "Saving..." : "Save"}
                          </button>
                          <button
                            onClick={handleCancel}
                            disabled={saving}
                            className="px-3 py-1 bg-gray-600 text-white text-xs rounded-md hover:bg-gray-700 disabled:bg-gray-500 disabled:cursor-not-allowed"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(org)}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-6 bg-blue-900/20 border border-blue-600 rounded-lg p-4">
        <h3 className="text-blue-200 font-semibold mb-2">💡 Setting Limits</h3>
        <ul className="text-blue-200 text-sm space-y-1">
          <li>
            • <strong>Trial Days:</strong> Number of trial days for the
            organization (0 = no trial)
          </li>
          <li>
            • <strong>Limits:</strong> Use{" "}
            <code className="bg-blue-900/50 px-1 rounded">-1</code> for
            unlimited, or set a specific number
          </li>
          <li>
            • <strong>AI Document Generation Limit:</strong> Maximum number of
            documents that can be generated using AI
          </li>
          <li>
            • <strong>Max Users:</strong> Maximum number of users the
            organization administrator can add
          </li>
          <li>
            • <strong>LLM $ / mo:</strong> Estimated monthly LLM spend budget
            (default $5). File upload / Text AI count caps are retired — spend
            is gated when <strong>Enforce</strong> is on.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default TenantManagement;
